import { sqliteTable, text, integer, index, type AnySQLiteColumn } from "drizzle-orm/sqlite-core";
import { dimensionTypes } from "./dimension-types";

// ============================================================================
// dimension_values — admin-defined values within a dimension type.
//
// EPIC #412 Phase 3 — standalone dimensions.
//
// Each row is a tag the user can attach to a JE/invoice allocation row via
// `accounting_je_item_allocations.dimension_value_id`. Tree structure mirrors
// `accounting_dimension_types` — values can be grouped (e.g. "Asia/Bangkok"
// nested under "Asia") so reports can roll up.
//
// Code uniqueness scope: (company_id, dimension_type_id, code). Two different
// dimension types can both have a "BKK" value without collision.
// ============================================================================

export const dimensionValues = sqliteTable("accounting_dimension_values", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  dimensionTypeId: text("dimension_type_id").notNull().references(() => dimensionTypes.id),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  parentId: text("parent_id").references((): AnySQLiteColumn => dimensionValues.id), // self-ref tree
  isGroup: integer("is_group").notNull().default(0),
  isActive: integer("is_active").notNull().default(1),
  createdBy: text("created_by"),
  createdAt: text("created_at").notNull(),
  updatedBy: text("updated_by"),
  updatedAt: text("updated_at").notNull(),
  deletedBy: text("deleted_by"),
  deletedAt: text("deleted_at"),
}, (table) => ({
  // Company-scoped active lookup, narrowed by dimension type — the
  // admin/list-views filter by typeId so this composite index covers them.
  dimValueCompanyTypeIdx: index("dim_value_company_type_idx").on(
    table.companyId,
    table.dimensionTypeId,
    table.isActive,
  ),
  // Tree traversal within a single type.
  dimValueParentIdx: index("dim_value_parent_idx").on(table.parentId),
}));

// Type exports
export type DimensionValue = typeof dimensionValues.$inferSelect;
export type NewDimensionValue = typeof dimensionValues.$inferInsert;
